/**
 * `location.settings.cardHolds` — a card hold (pre-auth card on file) for bar
 * tabs and table service (docs/CARD_HOLDS.md). SPARSE and SERVER-WRITTEN
 * (firestore rules: serverOnlySettingsBlocks; the only writer is the
 * card-hold settings route via the API). Absent ⇒ defaults, which leave the
 * feature OFF (`enabled: false`), so a location that never saved the block
 * behaves exactly as before the block existed.
 *
 * DARK: nothing reads `enabled` to move money until the tenant flag
 * `initiative.card_holds` is on as well (CARD_HOLDS_FLAG, `@order-up/common`).
 * The block may be saved while the flag is off; it takes effect when the flag
 * is switched on.
 *
 * Money is INTEGER cents. ONE resolver, `resolveCardHoldSettings`, for every
 * reader (API, core services, management card); the POS keeps a Dart port.
 */
export interface LocationCardHoldSettings {
  /** Master switch for the location. Default off. Turning it on requires a TRX card processor. */
  readonly enabled: boolean;
  /** Offer a hold on Tab Mode orders. Default on (when `enabled`). */
  readonly tabMode: boolean;
  /** Offer a hold on Table Service orders. Default on (when `enabled`). */
  readonly tableService: boolean;
  /** The amount authorized when the card is held, in cents. Default 2500 ($25.00). 100..50000. */
  readonly holdAmountCents: number;
  /** A new Tab Mode tab cannot be opened without holding a card. Default off. */
  readonly requireCardForTab: boolean;
  /**
   * Gratuity added, as a whole percent, when a held tab left open at the batch
   * cutoff is auto-closed (a walkout). Recorded as gratuity, which the payout
   * rules treat as the server's tip. Default 0. 0..30.
   */
  readonly walkoutGratuityPercent: number;
}

export const LOCATION_CARD_HOLD_SETTINGS_KEYS = [
  'enabled',
  'tabMode',
  'tableService',
  'holdAmountCents',
  'requireCardForTab',
  'walkoutGratuityPercent',
] as const;
export type LocationCardHoldSettingsKey = (typeof LOCATION_CARD_HOLD_SETTINGS_KEYS)[number];

/** Inclusive bounds of `holdAmountCents` ($1.00 .. $500.00). Zod, the route and the card read these. */
export const CARD_HOLD_AMOUNT_MIN_CENTS = 100;
export const CARD_HOLD_AMOUNT_MAX_CENTS = 50000;
/** Inclusive bounds of `walkoutGratuityPercent`. */
export const CARD_HOLD_WALKOUT_GRATUITY_MIN_PERCENT = 0;
export const CARD_HOLD_WALKOUT_GRATUITY_MAX_PERCENT = 30;

export const CARD_HOLD_SETTINGS_DEFAULTS: LocationCardHoldSettings = Object.freeze({
  enabled: false,
  tabMode: true,
  tableService: true,
  holdAmountCents: 2500,
  requireCardForTab: false,
  walkoutGratuityPercent: 0,
});

function intInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= min && value <= max;
}

/**
 * The block as every reader must see it: each key that is absent, of the wrong
 * type or out of range reads as its default. Never throws.
 */
export function resolveCardHoldSettings(block: unknown): LocationCardHoldSettings {
  const raw = block && typeof block === 'object' ? (block as Record<string, unknown>) : {};
  const bool = (key: 'enabled' | 'tabMode' | 'tableService' | 'requireCardForTab'): boolean =>
    typeof raw[key] === 'boolean' ? (raw[key] as boolean) : CARD_HOLD_SETTINGS_DEFAULTS[key];
  return {
    enabled: bool('enabled'),
    tabMode: bool('tabMode'),
    tableService: bool('tableService'),
    holdAmountCents: intInRange(raw.holdAmountCents, CARD_HOLD_AMOUNT_MIN_CENTS, CARD_HOLD_AMOUNT_MAX_CENTS)
      ? raw.holdAmountCents
      : CARD_HOLD_SETTINGS_DEFAULTS.holdAmountCents,
    requireCardForTab: bool('requireCardForTab'),
    walkoutGratuityPercent: intInRange(
      raw.walkoutGratuityPercent,
      CARD_HOLD_WALKOUT_GRATUITY_MIN_PERCENT,
      CARD_HOLD_WALKOUT_GRATUITY_MAX_PERCENT,
    )
      ? raw.walkoutGratuityPercent
      : CARD_HOLD_SETTINGS_DEFAULTS.walkoutGratuityPercent,
  };
}
